import React, { useLayoutEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { AppLayout } from "../components/layout/AppLayout";
import { MaskEffectHomepage } from "../components/shared/effects/MaskEffectVariants";
import { LandingMaskOverlay } from "../components/shared/effects/LandingMaskOverlay";
import { HistoryStorySection, HistoryRevealContent } from "../components/landing/HistoryStorySection";
import { ProblemSection } from "../components/landing/ProblemSection";
import { WendepunktSection } from "../components/landing/WendepunktSection";
import { BitcoinSolutionSection } from "../components/landing/BitcoinSolutionSection";
import { VertrauenSection } from "../components/landing/VertrauenSection";
import { AngebotSection } from "../components/landing/AngebotSection";
import { ContactFormSection } from "../components/contact/ContactFormSection";
import { FooterTW } from "../components/layout/FooterTW";
import { useLenis } from "../hooks/useLenis";
import { useAuth } from "../contexts/AuthContext";

// Landingpage-spezifische Hintergrundfarbe
const LANDING_BG = "#0d0d0d";
const DEFAULT_BG = "#030712";

export const HomePage: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const historyRef = useRef<HTMLElement>(null);

  // Initialize Lenis smooth scrolling
  useLenis();

  // Setze Body-Hintergrund auf Landingpage-Farbe (useLayoutEffect = synchron vor Paint)
  useLayoutEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    const originalHtmlBg = document.documentElement.style.backgroundColor;

    // Synchron setzen vor dem ersten Paint
    document.body.style.backgroundColor = LANDING_BG;
    document.documentElement.style.backgroundColor = LANDING_BG;

    // Auch theme-color Meta-Tag aktualisieren
    const meta = document.getElementById("theme-color-meta") as HTMLMetaElement;
    const originalMeta = meta?.content;
    if (meta) meta.content = LANDING_BG;

    // Beim Verlassen der Seite zurücksetzen
    return () => {
      document.body.style.backgroundColor = originalBg || DEFAULT_BG;
      document.documentElement.style.backgroundColor = originalHtmlBg || DEFAULT_BG;
      if (meta) meta.content = originalMeta || DEFAULT_BG;
    };
  }, []);

  // Eingeloggte User direkt zum Dashboard weiterleiten
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppLayout hideFooter={true} fullscreenHero={true} hideNavigation={true} style={{ backgroundColor: LANDING_BG }}>
      <Helmet>
        <title>RENDITEFUCHS | Bitcoin-Analysen, Elliott-Wellen & Beratung</title>
        <meta name="description" content="Bitcoin-Analysen und individuelle Bitcoin-Beratung aus Reutlingen/Tübingen. Elliott-Wellen-Prognosen und fundiertes Bitcoin-Wissen, zugeschnitten auf deine Situation." />
        <link rel="canonical" href="https://renditefuchs.de/" />
      </Helmet>
      <div className="min-h-screen landing-page-container select-none" style={{ backgroundColor: LANDING_BG }}>
        <h1 className="sr-only">
          RENDITEFUCHS – Bitcoin-Analysen, Elliott-Wellen-Theorie und individuelle Bitcoin-Beratung
        </h1>

        {/* Globaler Mask-Overlay: oranger Cursor klein default, groß über Text-Targets,
            mit Reveal-Inhalt pro registrierte Section. Liegt über die ganze Landingpage. */}
        <LandingMaskOverlay
          regions={[
            {
              id: "history",
              sectionRef: historyRef,
              content: ({ fadeOutProgress }) => (
                <HistoryRevealContent fadeOutProgress={fadeOutProgress} />
              ),
            },
          ]}
        />

        {/* Bitcoin Mask Effect - First Section */}
        <MaskEffectHomepage />

        {/* History Story Section - Germany */}
        <HistoryStorySection ref={historyRef} />

        {/* Spacer */}
        <div style={{ height: "clamp(12rem, 15vw, 24rem)", backgroundColor: LANDING_BG }} />

        {/* Problem Section */}
        <ProblemSection />

        {/* Wendepunkt Section - Turning Point */}
        <WendepunktSection />

        {/* Spacer */}
        <div style={{ height: "clamp(12rem, 15vw, 24rem)", backgroundColor: LANDING_BG }} />

        {/* Bitcoin Solution Section */}
        <BitcoinSolutionSection />

        {/* Spacer */}
        <div style={{ height: "clamp(12rem, 15vw, 24rem)", backgroundColor: LANDING_BG }} />

        {/* Angebot Section - Wie wir dir helfen */}
        <AngebotSection />

        {/* Spacer */}
        <div style={{ height: "clamp(12rem, 15vw, 24rem)", backgroundColor: LANDING_BG }} />

        {/* Testimonials Section */}
        <VertrauenSection />

        {/* Contact Form Section - Third Section with snap effect */}
        {/* Wrapper adds extra height for snap-and-release effect */}
        {/* Responsive: 100vh on short screens (< 950px), 110vh on tall screens */}
        <div className="relative contact-wrapper" style={{ backgroundColor: LANDING_BG }}>
          <ContactFormSection />
        </div>

        {/* Footer - mit #0d0d0d Hintergrund passend zur Landingpage */}
        <FooterTW style={{ backgroundColor: LANDING_BG }} variant="landing" />
      </div>
    </AppLayout>
  );
};
